// ABOUTME: Users tab for workspace detail page in admin panel.
// ABOUTME: Provides user list, search, add, and remove functionality.

import { useEffect, useState, useCallback } from 'react';
import {
  Users,
  Search,
  Plus,
  Trash2,
  Shield,
  User,
  Mail,
  Calendar,
  X,
  AlertTriangle,
  Check,
  UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { workspaceApi, userApi } from '@/services/api/adminClient';
import type { WorkspaceUser, UserRole } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface WorkspaceUsersTabProps {
  workspaceId: string;
}

export function WorkspaceUsersTab({ workspaceId }: WorkspaceUsersTabProps) {
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [addMode, setAddMode] = useState<'select' | 'create'>('select');

  // For selecting existing users
  const [allUsers, setAllUsers] = useState<WorkspaceUser[]>([]);
  const [allUsersTotal, setAllUsersTotal] = useState(0);
  const [allUsersPage, setAllUsersPage] = useState(1);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearchApplied, setUserSearchApplied] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('user');
  const MODAL_PAGE_SIZE = 10;

  // For creating new users
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('user');
  const [emailVerified, setEmailVerified] = useState(false);

  // Delete confirmation state
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const PAGE_SIZE = 20;

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await workspaceApi.getUsers(workspaceId, {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
      });
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, page, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load all users when modal opens
  const loadAllUsers = useCallback(async (searchTerm?: string, pageNum: number = 1) => {
    try {
      setAllUsersLoading(true);
      const response = await userApi.listAll({
        page: pageNum,
        page_size: MODAL_PAGE_SIZE,
        search: searchTerm || undefined,
        exclude_workspace_id: workspaceId,
      });
      setAllUsers(response.users);
      setAllUsersTotal(response.total);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setAllUsersLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (showAddModal && addMode === 'select') {
      loadAllUsers(userSearchApplied, allUsersPage);
    }
  }, [showAddModal, addMode, loadAllUsers, userSearchApplied, allUsersPage]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleUserSearch = () => {
    setUserSearchApplied(userSearchInput);
    setAllUsersPage(1);
  };

  const handleViewAllUsers = () => {
    setUserSearchInput('');
    setUserSearchApplied('');
    setAllUsersPage(1);
  };

  const handleUserSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleUserSearch();
    }
  };

  const handleAddExistingUser = async () => {
    if (!selectedUserId) return;

    try {
      setAddingUser(true);
      const newUser = await userApi.add(workspaceId, {
        user_id: selectedUserId,
        role: selectedRole,
      });
      setUsers((prev) => [newUser, ...prev]);
      setTotal((prev) => prev + 1);
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleAddNewUser = async () => {
    if (!newUserEmail || !newUserName) return;

    try {
      setAddingUser(true);
      const newUser = await userApi.add(workspaceId, {
        email: newUserEmail,
        name: newUserName,
        role: newUserRole,
        email_verified: emailVerified,
      });
      setUsers((prev) => [newUser, ...prev]);
      setTotal((prev) => prev + 1);

      const successMessage = emailVerified
        ? "User created! They'll receive an email to set their password."
        : "User created! They'll receive a verification email to set their password.";

      toast.success(successMessage);
      closeModal();
    } catch (error: any) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;

      let errorMessage: string;
      let showRetry = false;

      if (status === 409) {
        errorMessage = 'User with this email already exists';
      } else if (status === 429) {
        errorMessage = 'Auth0 rate limit exceeded. Please try again in a moment.';
        showRetry = true;
      } else if (status === 400) {
        errorMessage = `Invalid request: ${detail}`;
      } else {
        errorMessage = `Failed to create user: ${detail}`;
        showRetry = true;
      }

      toast.error(errorMessage, {
        duration: showRetry ? 6000 : 4000,
      });
    } finally {
      setAddingUser(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setAddMode('select');
    setSelectedUserId(null);
    setSelectedRole('user');
    setUserSearchInput('');
    setAllUsers([]);
    setNewUserEmail('');
    setNewUserName('');
    setNewUserRole('user');
    setEmailVerified(false);
    setAllUsersPage(1);
    setAllUsersTotal(0);
    setUserSearchApplied('');
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setDeletingUserId(userId);
      await userApi.remove(workspaceId, userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setTotal((prev) => prev - 1);
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-surface-800">
          Users ({total})
        </h3>
        <Button
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Add User
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full pl-10 pr-4 py-2.5 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm placeholder:text-surface-500 focus:outline-none focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
          />
        </div>
        <Button variant="secondary" onClick={handleSearch}>
          Search
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-light border border-error/30 rounded-sm p-3 text-sm text-error flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-auto hover:text-error/80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* User List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      ) : users.length === 0 ? (
        <Card variant="glass" className="p-8 text-center">
          <Users className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <p className="text-surface-600">
            {search ? 'No users found matching your search' : 'No users in this workspace'}
          </p>
          {!search && (
            <Button
              size="sm"
              className="mt-4"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddModal(true)}
            >
              Add First User
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {users.map((user) => (
            <Card key={user.id} variant="surface">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center overflow-hidden">
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-5 h-5 text-primary-600" />
                    )}
                  </div>

                  {/* User Info */}
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-surface-800">
                        {user.name || 'Unnamed User'}
                      </h4>
                      <RoleBadge role={user.role} />
                      {user.is_primary && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm bg-primary-50 text-primary-600">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-surface-500 mt-0.5">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </span>
                      {user.last_login_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Last login: {new Date(user.last_login_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {confirmDeleteId === user.id ? (
                    <>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        isLoading={deletingUserId === user.id}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDeleteId(user.id)}
                      className="text-error hover:text-error"
                      title="Remove user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-surface-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card variant="surface" className="w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-surface-800">Add User</h3>
              <button
                onClick={closeModal}
                className="text-surface-500 hover:text-surface-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mode Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setAddMode('select')}
                className={`flex-1 py-2.5 px-4 rounded-sm text-sm font-medium transition-colors ${
                  addMode === 'select'
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-100 text-surface-600 hover:text-surface-800'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                Select Existing
              </button>
              <button
                onClick={() => setAddMode('create')}
                className={`flex-1 py-2.5 px-4 rounded-sm text-sm font-medium transition-colors ${
                  addMode === 'create'
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-100 text-surface-600 hover:text-surface-800'
                }`}
              >
                <UserPlus className="w-4 h-4 inline mr-2" />
                Create New
              </button>
            </div>

            {addMode === 'select' ? (
              <div className="space-y-4">
                {/* Search Users */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={userSearchInput}
                      onChange={(e) => setUserSearchInput(e.target.value)}
                      onKeyDown={handleUserSearchKeyDown}
                      className="w-full pl-10 pr-4 py-2.5 rounded-sm bg-surface-100 border border-surface-200 text-surface-800 text-sm placeholder:text-surface-500 focus:outline-none focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                    />
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleUserSearch}>
                    Search
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleViewAllUsers}>
                    View All
                  </Button>
                </div>

                {/* Results info */}
                {allUsersTotal > 0 && (
                  <div className="text-xs text-surface-500">
                    {userSearchApplied ? (
                      <>Showing {allUsers.length} of {allUsersTotal} results for "{userSearchApplied}"</>
                    ) : (
                      <>Showing {allUsers.length} of {allUsersTotal} users</>
                    )}
                  </div>
                )}

                {/* User List */}
                <div className="max-h-64 overflow-y-auto rounded-sm border border-surface-200">
                  {allUsersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                    </div>
                  ) : allUsers.length === 0 ? (
                    <div className="py-8 text-center text-sm text-surface-500">
                      {userSearchApplied ? 'No users found' : 'No users available to add'}
                    </div>
                  ) : (
                    <div className="divide-y divide-surface-200">
                      {allUsers.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => setSelectedUserId(user.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            selectedUserId === user.id
                              ? 'bg-primary-50'
                              : 'hover:bg-surface-100'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                            {user.avatar_url ? (
                              <img
                                src={user.avatar_url}
                                alt={user.name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-4 h-4 text-primary-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-surface-800 truncate">
                              {user.name || 'Unnamed User'}
                            </p>
                            <p className="text-xs text-surface-500 truncate">
                              {user.email}
                            </p>
                          </div>
                          {selectedUserId === user.id && (
                            <Check className="w-5 h-5 text-primary-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination for user list */}
                {Math.ceil(allUsersTotal / MODAL_PAGE_SIZE) > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAllUsersPage((p) => Math.max(1, p - 1))}
                      disabled={allUsersPage === 1 || allUsersLoading}
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-surface-600">
                      Page {allUsersPage} of {Math.ceil(allUsersTotal / MODAL_PAGE_SIZE)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAllUsersPage((p) => p + 1)}
                      disabled={allUsersPage >= Math.ceil(allUsersTotal / MODAL_PAGE_SIZE) || allUsersLoading}
                    >
                      Next
                    </Button>
                  </div>
                )}

                {/* Role Selection */}
                {selectedUserId && (
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Role
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                      className="w-full px-4 py-3 rounded-sm bg-surface-100 border border-surface-200 text-surface-800 text-sm"
                    >
                      <option value="user">User</option>
                      <option value="workspace-admin">Admin</option>
                    </select>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="user@example.com"
                />
                <Input
                  label="Name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="John Doe"
                />
                <div>
                  <label className="block text-sm font-medium text-surface-600 mb-1.5">
                    Role
                  </label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as UserRole)}
                    className="w-full px-4 py-3 rounded-sm bg-surface-100 border border-surface-200 text-surface-800 text-sm"
                  >
                    <option value="user">User</option>
                    <option value="workspace-admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailVerified}
                      onChange={(e) => setEmailVerified(e.target.checked)}
                      className="rounded border-surface-200 text-primary-600 focus:ring-primary-600/25"
                    />
                    <span className="text-sm font-medium text-surface-800">
                      Mark email as verified
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-surface-500">
                    Check this if the user's email is already verified (skips verification email)
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="ghost" onClick={closeModal}>
                Cancel
              </Button>
              {addMode === 'select' ? (
                <Button
                  onClick={handleAddExistingUser}
                  isLoading={addingUser}
                  disabled={!selectedUserId}
                >
                  Add User
                </Button>
              ) : (
                <Button
                  onClick={handleAddNewUser}
                  isLoading={addingUser}
                  disabled={!newUserEmail || !newUserName}
                >
                  Create User
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: UserRole }) {
  if (role === 'workspace-admin') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm bg-warning-light text-warning">
        <Shield className="w-3 h-3" />
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm bg-surface-100 text-surface-500">
      <User className="w-3 h-3" />
      User
    </span>
  );
}

export default WorkspaceUsersTab;
